import React from 'react';
import CurrencySelector from './CurrencySelector';
import AmountSlider from './AmountSlider';
import {
  MAX_INVESTMENT_AMOUNT_USD,
  MIN_INVESTMENT_AMOUNT,
} from '../../../constants/profileFieldLimits';
import { loadExchangeRates } from '../../../utils/loadExchangeRates';
import { maxInvestmentInCurrency, minStartupAskInCurrency } from '../../../utils/minStartupAsk';

interface AmountSelectorProps {
  currency: string;
  amount: number;
  onCurrencyChange: (cur: string) => void;
  onAmountChange: (val: number) => void;
  multiplier?: number;
  min?: number;
  max?: number;
  step?: number;
}

function snapToStep(value: number, step: number): number {
  if (!Number.isFinite(value) || step <= 0) return value;
  return Math.round(value / step) * step;
}

/**
 * Startup investment amount. `amount` is always the real ask (what gets saved).
 * Default ceiling is max/10 (~20M). +10x multiplies the ask by 10 and unlocks ~200M.
 * Turning +10x off never divides — it only re-locks the ~20M ceiling.
 */
const AmountSelector: React.FC<AmountSelectorProps> = ({
  currency,
  amount,
  onCurrencyChange,
  onAmountChange,
  multiplier = 1,
  min = MIN_INVESTMENT_AMOUNT,
  max = MAX_INVESTMENT_AMOUNT_USD,
  step = 1000,
}) => {
  const [isMultiplierActive, setIsMultiplierActive] = React.useState(false);
  const [exchangeRates, setExchangeRates] = React.useState<Record<string, number> | null>(null);
  const [loadingRates, setLoadingRates] = React.useState(true);
  const [previousCurrency, setPreviousCurrency] = React.useState<string>(currency || 'eur');

  const safeAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;

  React.useEffect(() => {
    let cancelled = false;
    loadExchangeRates()
      .then(rates => {
        if (!cancelled) setExchangeRates(rates);
      })
      .finally(() => {
        if (!cancelled) setLoadingRates(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const localMin = minStartupAskInCurrency(currency || 'usd', exchangeRates);
  const localMax = maxInvestmentInCurrency(currency || 'usd', exchangeRates, max);
  const effectiveMin = Math.max(min, localMin);
  const absoluteMax = Math.max(localMax, effectiveMin);

  const lowMax =
    multiplier > 1 ? Math.max(step, snapToStep(absoluteMax / multiplier, step)) : absoluteMax;
  // Saved asks above the default ceiling stay on the unlocked scale.
  const unlocked = (isMultiplierActive || safeAmount > lowMax) && multiplier > 1;
  const activeMax = unlocked ? absoluteMax : lowMax;

  React.useEffect(() => {
    if (safeAmount > lowMax && multiplier > 1 && !isMultiplierActive) {
      setIsMultiplierActive(true);
    }
  }, [safeAmount, lowMax, multiplier, isMultiplierActive]);

  const handleMultiplierToggle = () => {
    if (multiplier <= 1) return;

    if (unlocked) {
      // Turn off: keep the ask; only clamp if above the ~20M ceiling. Never ÷10.
      if (safeAmount > lowMax) {
        onAmountChange(lowMax);
      }
      setIsMultiplierActive(false);
      return;
    }

    // Turn on: multiply ask by 10 (capped) and unlock the ~200M ceiling.
    const next =
      safeAmount > 0
        ? Math.min(absoluteMax, snapToStep(safeAmount * multiplier, step))
        : safeAmount;
    if (next !== safeAmount) {
      onAmountChange(next);
    }
    setIsMultiplierActive(true);
  };

  const handleCurrencyChange = (newCurrency: string) => {
    let nextAmount = safeAmount;
    if (
      exchangeRates &&
      previousCurrency &&
      newCurrency !== previousCurrency &&
      typeof exchangeRates[previousCurrency] === 'number' &&
      typeof exchangeRates[newCurrency] === 'number'
    ) {
      const rateFrom = exchangeRates[previousCurrency];
      const rateTo = exchangeRates[newCurrency];
      nextAmount = Math.round((safeAmount / rateFrom) * rateTo);
    }

    const nextMin = Math.max(min, minStartupAskInCurrency(newCurrency, exchangeRates));
    if (nextAmount > 0 && nextAmount < nextMin) {
      nextAmount = nextMin;
    }
    if (nextAmount !== safeAmount) {
      onAmountChange(nextAmount);
    }

    setPreviousCurrency(newCurrency);
    onCurrencyChange(newCurrency);
  };

  return (
    <div className="space-y-4">
      <CurrencySelector value={currency || 'eur'} onChange={handleCurrencyChange} />
      {loadingRates && (
        <div className="text-sm text-gray-400 text-left">Loading exchange rates...</div>
      )}
      <AmountSlider
        value={Math.min(activeMax, Math.max(0, safeAmount))}
        onChange={val => onAmountChange(Math.min(activeMax, Math.max(0, val)))}
        currency={currency}
        multiplier={multiplier}
        min={effectiveMin}
        max={activeMax}
        step={step}
        isMultiplierActive={unlocked}
        onMultiplierToggle={handleMultiplierToggle}
        multiplyDisplay={false}
        showManualInput
      />
    </div>
  );
};

export default AmountSelector;
